import { Component, computed, inject, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { LOTTO_STATE, LottoDetails, LottoV2Query, SortOption } from '@fgb/core';
import { SortBarModule, SortBarOption, SortBarOrder, SortOrder } from '@fgb/portal-component-library/src/lib/shared/sort-bar';
import { CommonModule } from '@angular/common';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { LottosListItemV2Component } from '../lottos-list-item-v2/lottos-list-item-v2.component';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { PipesModule } from '@fgb/portal-component-library/src/lib/pipes';

export enum LottoListSortOption {
  Ordinal,
  Favourite,
  Points,
}

@Component({
  selector: 'fgbcl-lottos-list-v2',
  standalone: true,
  imports: [
    CommonModule,
    FGBSharedModule,
    DirectivesModule,
    LottosListItemV2Component,
    SortBarModule,
    LoadingModule,
    InlineSVGModule,
    PipesModule,
  ],
  templateUrl: './lottos-list-v2.component.html',
  styleUrl: './lottos-list-v2.component.scss',
})
export class LottosListV2Component implements OnInit {
  @Input() isList: boolean = false;
  @Input() defaultSort: LottoListSortOption = LottoListSortOption.Favourite;
  @Input() featuredIcon: string = 'star';

  readonly sortBarOptions: SortBarOption[] = [
    {
      propertyToSort: 'PurchasePrice',
      shouldToggle: false,
      order: SortOrder.Descending,
      labelDESC: 'lotto.high.low',
    },
    {
      propertyToSort: 'PurchasePrice',
      shouldToggle: false,
      order: SortOrder.Ascending,
      labelASC: 'lotto.low.high',
    },
    {
      propertyToSort: 'AvailableCount',
      shouldToggle: true,
      order: SortOrder.Descending,
      labelASC: 'lotto.limited.availability',
      labelDESC: 'lotto.high.availability',
    },
    {
      propertyToSort: 'Title',
      shouldToggle: true,
      order: SortOrder.Descending,
      labelASC: 'lotto.a.z',
      labelDESC: 'lotto.z.a',
    },
  ];

  private readonly lottoQuery = inject(LottoV2Query);
  public readonly isLoaded = this.lottoQuery.isLoaded;
  private sortOption: WritableSignal<SortOption> = signal(this.getDefaultSortByOption(this.defaultSort));

  readonly lottos = computed(() => {
    const sortBy = this.sortOption();
    return this.lottoQuery.getLottos({ sortBy })();
  });

  ngOnInit() {
    this.onSortChange(null);
  }

  onSortChange(selectedOption: { property: string; order: SortBarOrder } | null) {
    if (!selectedOption) {
      this.sortOption.set(this.getDefaultSortByOption(this.defaultSort));
      return;
    }
    this.sortOption.set({ key: selectedOption.property, order: selectedOption.order });
  }

  private getDefaultSortByOption(sort: LottoListSortOption) {
    switch (sort) {
      case LottoListSortOption.Favourite:
        return (a: LottoDetails, b: LottoDetails) => {
          const map = this.lottoQuery.uiStatusMap();
          const aFeatured = map.get(a.LottoId)?.statusText === LOTTO_STATE.FEATURED ? 1 : 0;
          const bFeatured = map.get(b.LottoId)?.statusText === LOTTO_STATE.FEATURED ? 1 : 0;
          return bFeatured - aFeatured;
        };
      case LottoListSortOption.Ordinal:
        return { key: 'Ordinal', order: 'asc' as const };
      case LottoListSortOption.Points:
        return { key: 'PurchasePrice', order: 'asc' as const };
      default:
        return undefined;
    }
  }
}
